import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { bookings as bookingsApi, payments as paymentsApi } from "../services/api";

const STATUS_LABEL = {
  pending:   "待付款",
  confirmed: "已確認",
  cancelled: "已取消",
  completed: "已完成",
  refunded:  "已退款",
};

const STATUS_COLOR = {
  pending:   { background: "#FEF3C7", color: "#92400E" },
  confirmed: { background: "#D1FAE5", color: "#065F46" },
  cancelled: { background: "#F3F4F6", color: "#6B7280" },
  completed: { background: "#DBEAFE", color: "#1E40AF" },
  refunded:  { background: "#F3F4F6", color: "#6B7280" },
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return date.toLocaleDateString("zh-TW", { year: "numeric", month: "long", day: "numeric", weekday: "short" });
}

function formatTime(t) {
  if (!t) return "";
  return t.slice(0, 5);
}

function formatDeadline(deadline) {
  if (!deadline) return null;
  const now  = new Date();
  const end  = new Date(deadline);
  const diff = end - now;
  if (diff <= 0) return "已逾期";
  const hours   = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `剩 ${days} 天`;
  }
  return `剩 ${hours}h ${minutes}m`;
}

export default function BookingsPage() {
  const navigate = useNavigate();
  const [bookings, setBookings]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [filter, setFilter]       = useState("all");
  const [cancelling, setCancelling] = useState(null);
  const [paying, setPaying]       = useState(null);

  useEffect(() => {
    bookingsApi.list()
      .then(setBookings)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = bookings.filter(b => {
    if (filter === "all")      return true;
    if (filter === "pending")  return b.status === "pending";
    if (filter === "active")   return b.status === "confirmed";
    if (filter === "inactive") return ["cancelled", "completed", "refunded"].includes(b.status);
    return true;
  });

  const handleCancel = async (id) => {
    const booking = bookings.find(b => b.id === id);
    const courseDate = new Date(booking.booking_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysUntilCourse = Math.floor((courseDate - today) / (1000 * 60 * 60 * 24));

    let refundRule;
    if (booking.payment_status !== "paid") {
      refundRule = "此預約尚未付款，取消不涉及退款。";
    } else if (daysUntilCourse >= 7) {
      refundRule = "課前 7 天以上取消：全額退款（將扣除 Stripe 手續費）。";
    } else if (daysUntilCourse >= 3) {
      refundRule = "課前 3–7 天取消：退款 50%（將扣除 Stripe 手續費）。";
    } else {
      refundRule = "課前 3 天以內取消：不可退款。";
    }

    if (!window.confirm(`確定要取消此預約嗎？\n\n${refundRule}`)) return;

    setCancelling(id);
    try {
      const result = await bookingsApi.cancel(id);
      setBookings(prev => prev.map(b => b.id === id ? { ...b, status: "cancelled" } : b));
      if (result.refund_amount > 0) {
        alert(`預約已取消。\n退款金額：NT$ ${result.refund_amount.toLocaleString()}\nStripe 手續費：NT$ ${result.stripe_fee.toLocaleString()}`);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setCancelling(null);
    }
  };

  const handlePay = async (bookingId) => {
    setPaying(bookingId);
    try {
      const { checkout_url } = await paymentsApi.createSession(bookingId);
      window.location.href = checkout_url;
    } catch (err) {
      alert(err.message);
      setPaying(null);
    }
  };

  if (loading) return (
    <div style={{ textAlign: "center", padding: 80 }}>
      <span className="spinner" style={{ margin: "0 auto" }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "40px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
        <h1 style={{ fontSize: 28 }}>我的預約</h1>
        <button className="btn btn-primary" style={{ height: 40, padding: "0 20px" }}
          onClick={() => navigate("/booking")}>
          新增預約
        </button>
      </div>

      {/* 篩選 */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {[
          { key: "all",      label: "全部" },
          { key: "pending",  label: "待付款" },
          { key: "active",   label: "已確認" },
          { key: "inactive", label: "已結束" },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className="btn"
            style={{
              height: 36, padding: "0 16px", fontSize: 13,
              background: filter === f.key ? "var(--ink)" : "var(--white)",
              color:      filter === f.key ? "var(--white)" : "var(--ink)",
              border:     `1.5px solid ${filter === f.key ? "var(--ink)" : "var(--stone)"}`,
            }}>
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: "#FEF2F2", color: "var(--red)", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: "center", color: "var(--muted)" }}>
          {filter === "all" ? "目前沒有任何預約" : "此分類沒有預約"}
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {filtered.map(b => {
            const deadline = b.status === "pending" ? formatDeadline(b.payment_deadline) : null;
            const colors   = STATUS_COLOR[b.status] || STATUS_COLOR.cancelled;

            return (
              <div key={b.id} className="card" style={{ padding: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                  <div>
                    <span style={{ fontSize: 13, color: "var(--muted)", marginRight: 8 }}>預約 #{b.id}</span>
                    <span style={{
                      fontSize: 12, fontWeight: 500, padding: "3px 10px",
                      borderRadius: 20, ...colors,
                    }}>
                      {STATUS_LABEL[b.status] || b.status}
                    </span>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 18 }}>
                    NT$ {Number(b.total_price).toLocaleString()}
                  </div>
                </div>

                {/* 預約資訊 */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 2 }}>日期</div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{formatDate(b.booking_date)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 2 }}>時間</div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>
                      {formatTime(b.package_start)} – {formatTime(b.package_end)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 2 }}>方案</div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{b.package_name || "—"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 2 }}>教練</div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{b.coach_name || "確認中"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 2 }}>程度</div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>
                      {{ beginner: "初學者", intermediate: "中級", advanced: "進階" }[b.student_level] || "—"}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 2 }}>付款狀態</div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>
                      {b.payment_status === "paid" ? "✅ 已付款" : "未付款"}
                    </div>
                  </div>
                </div>

                {/* 付款截止 */}
                {deadline && (
                  <div style={{
                    background: deadline === "已逾期" ? "#FEF2F2" : "#FEF3C7",
                    color: deadline === "已逾期" ? "var(--red)" : "#92400E",
                    borderRadius: 8, padding: "8px 14px", fontSize: 13,
                    marginBottom: 16,
                  }}>
                    付款截止：{deadline === "已逾期" ? "已逾期，預約將自動取消" : deadline}
                  </div>
                )}

                {/* 操作按鈕 */}
                <div style={{ display: "flex", gap: 10 }}>
                  {b.status === "pending" && b.payment_status !== "paid" && (
                    <button
                      className="btn btn-primary"
                      style={{ height: 38, padding: "0 20px", fontSize: 14 }}
                      disabled={paying === b.id}
                      onClick={() => handlePay(b.id)}
                    >
                      {paying === b.id
                        ? <span className="spinner" style={{ borderTopColor: "var(--white)" }} />
                        : "前往付款"}
                    </button>
                  )}
                  {["pending", "confirmed"].includes(b.status) && (
                    <button
                      className="btn"
                      style={{ height: 38, padding: "0 16px", fontSize: 14, color: "var(--red)", border: "1.5px solid var(--red)" }}
                      disabled={cancelling === b.id}
                      onClick={() => handleCancel(b.id)}
                    >
                      {cancelling === b.id ? "取消中..." : "取消預約"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}